/* eslint-disable array-callback-return */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import screenerSlice, {
  screenerEffects,
  screenerSelectors,
} from 'features/screener/slice';
import AutoComplete from '@deprecated-ui/watchlist/AutoComplete';
import color from '@deprecated-ui/_style/color';

const { Option } = AutoComplete;

const SAutoComplete = styled(AutoComplete)`
  .ant-select-selection-placeholder {
    font-family: var(--font-bold);
    color: ${color.downgray};
  }
`;

const flattenArray = (array, key) =>
  array.reduce((result, next) => {
    if (next[key].length === 0) {
      result.push(next);
    }
    if (next[key]) {
      result = result.concat(flattenArray(next[key], key));
    }
    return result;
  }, []);

const addUniqueKey = (dataArr) =>
  dataArr.map((el, index) => ({
    ...el,
    UUID: index,
  }));

const convertMetricDataToAutoSuggestions = (data: any[]) => {
  const result = [];
  data.forEach((item) => {
    result.push({
      name: item.fitem_name,
      id: item.fitem_id,
      UUID: item.UUID,
    });
  });
  return result;
};

const AutoSuggestion = ({
  selectedValue,
  type,
  paddingX,
  paddingT,
  width,
  placeholder = 'Search . . .',
  onClickSuggestion,
}: {
  selectedValue: string;
  type: string;
  paddingX: string;
  paddingT: string;
  width?: string;
  placeholder?: string;
  onClickSuggestion: (id: number, name: string) => void;
}) => {
  const [optionSuggestions, setOptionSuggestions] = useState([]);
  const [resultSuggestions, setResultSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState<string>('');

  const { screenerPreset, screenerFinancialMetric, screenerUtils } =
    useSelector(screenerSelectors);

  const {
    isModalFinancialMetricActive,
    isModalPresetScreenerActive,
    isModalAddColumnActive,
  } = screenerUtils;

  const { getScreener } = screenerEffects;

  const {
    setScreenerModal,
    setIsModalFinancialMetricActive,
    setIsModalPresetScreenerActive,
    setIsModalAddColumnActive,
    resetFormState,
  } = screenerSlice.actions;

  const dispatch = useDispatch();

  const onTypeHandler = (keyword = '') => {
    const lowerCaseKeyword = keyword?.toLowerCase();
    const result = optionSuggestions.filter(
      (option) => option.name.toLowerCase().includes(lowerCaseKeyword),
      // eslint-disable-next-line function-paren-newline
    );
    setInputValue(keyword);
    onClickSuggestion(null, keyword);

    if (keyword?.length > 0) {
      setResultSuggestions(result);
    } else {
      setResultSuggestions([]);
    }
  };

  const onClickHandler = (value, option) => {
    const { id, name } = option;

    // execute if autocomplete component mount in Modal: Preset
    if (isModalPresetScreenerActive) {
      setInputValue(null);
      dispatch(resetFormState());
      dispatch(getScreener({ screenerID: id, type: 'TEMPLATE_TYPE_GURU' }));
      dispatch(setIsModalPresetScreenerActive(false));
    }

    // execute if autocomplete component mount in Modal: Financial Metric
    if (isModalFinancialMetricActive) {
      setInputValue(null);
      dispatch(setScreenerModal({ screenerType: 'financial', id, name }));
      dispatch(setIsModalFinancialMetricActive(false));
    }

    // execute if autocomplete component mount in Modal: Add column
    if (isModalAddColumnActive) {
      setInputValue(null);
      dispatch(setScreenerModal({ screenerType: 'column', id, name }));
      dispatch(setIsModalAddColumnActive(false));
    }

    // execute if autocomplete component mount in screener rules (when user on new screener form)
    if (
      !isModalFinancialMetricActive &&
      !isModalPresetScreenerActive &&
      !isModalAddColumnActive
    ) {
      onClickSuggestion(id, name);
    }

    setResultSuggestions([]);
  };

  useEffect(() => {
    setInputValue(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    const { data: dataPreset } = screenerPreset;
    const { data: dataFinancial } = screenerFinancialMetric;
    if (type === 'preset' && dataPreset.length > 0) {
      const flattedScreener = flattenArray(dataPreset, 'childs');
      const withUnique = addUniqueKey(flattedScreener);
      setOptionSuggestions(withUnique);
    }
    if (type === 'financial' && dataFinancial.length > 0) {
      const flattedFinancial = flattenArray(dataFinancial, 'child');
      const withUnique = addUniqueKey(flattedFinancial);
      const convertedData = convertMetricDataToAutoSuggestions(withUnique);
      setOptionSuggestions(convertedData);
    }
  }, [screenerPreset, screenerFinancialMetric, type]);

  return (
    <SAutoComplete
      width={width}
      bordered
      // @ts-expect-error
      px={paddingX}
      pt={paddingT}
      placeholder={placeholder}
      onSearch={onTypeHandler}
      onSelect={onClickHandler}
      value={inputValue}
    >
      {resultSuggestions.map(({ name, id, UUID }) => (
        // @ts-expect-error
        <Option key={UUID} id={id} name={name}>
          {name}
        </Option>
      ))}
    </SAutoComplete>
  );
};

AutoSuggestion.defaultProps = {
  selectedValue: null,
  type: 'preset',
  paddingX: '12px',
  paddingT: '8px',
  width: '100%',
  placeholder: 'Search . . .',
  onClickSuggestion: () => {},
};

AutoSuggestion.propTypes = {
  selectedValue: PropTypes.string,
  type: PropTypes.string,
  paddingX: PropTypes.string,
  paddingT: PropTypes.string,
  onClickSuggestion: PropTypes.func,
};

export default AutoSuggestion;
